import { PublicLayout } from "@/components/PublicLayout";
import { SEOHead } from "@/components/SEOHead";
import { ModeBadge } from "@/components/figures/ModeBadge";
import { SourceLine } from "@/components/figures/SourceLine";
import { fieldNotes } from "@/data/fieldNotes";

function formatDate(iso: string) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

const FieldNotesPage = () => {
  const approved = fieldNotes
    .filter((n) => n.approved)
    .sort((a, b) => b.date.localeCompare(a.date));
  const upcoming = approved.filter((n) => n.upcoming);
  const past = approved.filter((n) => !n.upcoming);

  return (
    <PublicLayout>
      <SEOHead
        title="Field Notes · David Isei"
        description="What happened, when, and how I know. Dated notes on Hawaiʻi healthcare announcements, briefings, and public events, each with its source and its mode."
        url="https://davidisei.com/field-notes"
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-liberty-gold">
            Field Notes
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl">
            Field Notes
          </h1>
          <p className="mt-5 text-base leading-relaxed text-foreground/80">
            What happened, when, and how I know. Each note carries its source
            and its mode. Public events and announcements only; the fact of a
            meeting, never its contents.
          </p>

          {upcoming.length > 0 && (
            <div className="mt-10 rounded-xl border border-liberty-gold/40 bg-card/50 p-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-liberty-gold">
                Coming up
              </p>
              <ul className="mt-4 space-y-4">
                {upcoming.map((note) => (
                  <li key={note.id}>
                    <div className="flex flex-wrap items-center gap-3">
                      <time
                        dateTime={note.date}
                        className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground"
                      >
                        {formatDate(note.date)}
                      </time>
                      <ModeBadge mode={note.mode} />
                    </div>
                    <h2 className="mt-2 font-display text-lg font-bold text-foreground">
                      {note.title}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-foreground/80">
                      {note.body}
                    </p>
                    <div className="mt-3">
                      <SourceLine
                        source={note.source}
                        sourceUrl={note.sourceUrl}
                        retrievedOn={note.date}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ol className="mt-10 space-y-6">
            {past.map((note) => (
              <li
                key={note.id}
                className="rounded-xl border border-border/60 bg-card/60 p-5 sm:p-6"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <time
                    dateTime={note.date}
                    className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {formatDate(note.date)}
                  </time>
                  <ModeBadge mode={note.mode} />
                </div>
                <h2 className="mt-3 font-display text-xl font-bold text-foreground">
                  {note.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground/80">
                  {note.body}
                </p>
                <div className="mt-4">
                  <SourceLine
                    source={note.source}
                    sourceUrl={note.sourceUrl}
                    retrievedOn={note.date}
                  />
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </PublicLayout>
  );
};

export default FieldNotesPage;
